import Button from '@blaze-react/button';
import Input from '@blaze-react/input';
import Select from '@blaze-react/select';
import { useToasts } from '@blaze-react/toaster';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { PRIVACY } from '../../constants';
import {
  checkValidEmail,
  RESIDENCE_COUNTRIES,
  RESIDENCE_HIGH_REGULATION_COUNTRIES,
  pushToDataLayer,
  submitSalesforceNewsletter,
  userRoleOptions
} from '../../utils';

const roleOptions = [['', 'Which category best describes you?'], ...userRoleOptions];

const countryOfResidenceOptions = [
  ['', 'What is your country of residence?'],
  ...RESIDENCE_COUNTRIES.map(country => [country, country])
];

const FullPageForm = ({
  productBriefing,
  productBusinessNews,
  productDeepDive,
  productEvents,
  productMarketing,
  productPodcast,
  subtitle,
  title
}) => {
  const [submitState, setSubmitState] = useState(null);
  const { addToast } = useToasts();
  const [values, setValues] = useState({
    additionalTermsAccepted: null,
    countryOfResidence: '',
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    termsAccepted: false,
    checkProductBriefing: false,
    checkProductBusinessNews: false,
    checkProductDeepDive: false,
    checkProductEvents: false,
    checkProductMarketing: false,
    checkProductPodcast: false
  });

  const atLeastOneProductChecked = ({
    checkProductBriefing,
    checkProductBusinessNews,
    checkProductDeepDive,
    checkProductEvents,
    checkProductMarketing,
    checkProductPodcast
  }) =>
    checkProductBriefing ||
    checkProductBusinessNews ||
    checkProductDeepDive ||
    checkProductEvents ||
    checkProductMarketing ||
    checkProductPodcast;

  const {
    additionalTermsAccepted,
    countryOfResidence,
    firstName,
    lastName,
    email,
    role,
    termsAccepted,
    checkProductBriefing,
    checkProductBusinessNews,
    checkProductDeepDive,
    checkProductEvents,
    checkProductMarketing,
    checkProductPodcast
  } = values;

  const roleValid = role && role !== 'Please choose...';
  let termsValid = RESIDENCE_COUNTRIES.includes(countryOfResidence) && !!termsAccepted;
  if (
    RESIDENCE_HIGH_REGULATION_COUNTRIES.includes(countryOfResidence) &&
    additionalTermsAccepted === null
  ) {
    termsValid = false;
  }

  const isFormFull = !!(
    firstName &&
    lastName &&
    roleValid &&
    checkValidEmail(email) &&
    atLeastOneProductChecked(values) &&
    termsValid
  );

  const buttonClass = classnames('newsletter-signup__submit', {
    'newsletter-signup__submit--disabled': !isFormFull
  });

  const briefingOptionClass = classnames(
    'newsletter-signup__full-page-option newsletter-signup__full-page-option--briefing',
    {
      'newsletter-signup__full-page-option--checked': checkProductBriefing
    }
  );

  const businessNewsOptionClass = classnames(
    'newsletter-signup__full-page-option newsletter-signup__full-page-option--business-news',
    {
      'newsletter-signup__full-page-option--checked': checkProductBusinessNews
    }
  );

  const deepDiveOptionClass = classnames(
    'newsletter-signup__full-page-option newsletter-signup__full-page-option--deep-dive',
    {
      'newsletter-signup__full-page-option--checked': checkProductDeepDive
    }
  );

  const eventsOptionClass = classnames(
    'newsletter-signup__full-page-option newsletter-signup__full-page-option--events',
    {
      'newsletter-signup__full-page-option--checked': checkProductEvents
    }
  );

  const marketingOptionClass = classnames(
    'newsletter-signup__full-page-option newsletter-signup__full-page-option--marketing',
    {
      'newsletter-signup__full-page-option--checked': checkProductMarketing
    }
  );

  const podcastOptionClass = classnames(
    'newsletter-signup__full-page-option  newsletter-signup__full-page-option--podcast',
    {
      'newsletter-signup__full-page-option--checked': checkProductPodcast
    }
  );

  const showAdditionalTerms = RESIDENCE_HIGH_REGULATION_COUNTRIES.includes(countryOfResidence);

  const handleChange = ({ target: { name: targetName } }, value) => {
    setValues({
      additionalTermsAccepted,
      countryOfResidence,
      firstName,
      lastName,
      email,
      role,
      termsAccepted,
      checkProductBriefing,
      checkProductBusinessNews,
      checkProductDeepDive,
      checkProductEvents,
      checkProductMarketing,
      checkProductPodcast,
      [targetName]: value
    });
  };

  const handleCountryOfResidenceChange = value => {
    setValues({
      additionalTermsAccepted: null,
      countryOfResidence: value,
      firstName,
      lastName,
      email,
      role,
      termsAccepted,
      checkProductBriefing,
      checkProductBusinessNews,
      checkProductDeepDive,
      checkProductEvents,
      checkProductMarketing,
      checkProductPodcast
    });
  };

  const handleCheckboxChange = targetName => {
    setValues({
      additionalTermsAccepted,
      countryOfResidence,
      firstName,
      lastName,
      email,
      role,
      termsAccepted,
      checkProductBriefing:
        targetName === 'checkProductBriefing' ? !checkProductBriefing : checkProductBriefing,
      checkProductBusinessNews:
        targetName === 'checkProductBusinessNews'
          ? !checkProductBusinessNews
          : checkProductBusinessNews,
      checkProductDeepDive:
        targetName === 'checkProductDeepDive' ? !checkProductDeepDive : checkProductDeepDive,
      checkProductEvents:
        targetName === 'checkProductEvents' ? !checkProductEvents : checkProductEvents,
      checkProductMarketing:
        targetName === 'checkProductMarketing' ? !checkProductMarketing : checkProductMarketing,
      checkProductPodcast:
        targetName === 'checkProductPodcast' ? !checkProductPodcast : checkProductPodcast
    });
  };

  const handleTermsCheckboxChange = () => {
    setValues({
      additionalTermsAccepted,
      countryOfResidence,
      firstName,
      lastName,
      email,
      role,
      checkProductBriefing,
      checkProductBusinessNews,
      checkProductDeepDive,
      checkProductEvents,
      checkProductMarketing,
      checkProductPodcast,
      termsAccepted: !termsAccepted
    });
  };

  const handleAdditionalTermsChange = val => {
    setValues({
      additionalTermsAccepted: val,
      countryOfResidence,
      firstName,
      lastName,
      email,
      role,
      checkProductBriefing,
      checkProductBusinessNews,
      checkProductDeepDive,
      checkProductEvents,
      checkProductMarketing,
      checkProductPodcast,
      termsAccepted
    });
  };

  useEffect(() => {
    const doSubmit = async () => {
      if (submitState === null) return;

      setSubmitState(null);

      const products = [];
      if (submitState.values.checkProductBriefing) {
        products.push('briefing');
      }

      if (submitState.values.checkProductBusinessNews) {
        products.push('businessNews');
      }

      if (submitState.values.checkProductDeepDive) {
        products.push('deepDive');
      }

      if (submitState.values.checkProductEvents) {
        products.push('events');
      }

      if (submitState.values.checkProductMarketing) {
        products.push('marketing');
      }

      if (submitState.values.checkProductPodcast) {
        products.push('podcast');
      }

      try {
        await submitSalesforceNewsletter(
          submitState.values.firstName,
          submitState.values.lastName,
          submitState.values.email,
          products,
          {
            country: submitState.values.countryOfResidence,
            additionalTermsAccepted: submitState.values.additionalTermsAccepted
          },
          submitState.values.role
        );

        const basePayload = submitState.values.role
          ? { user: { role: submitState.values.role } }
          : {};

        products.forEach(product =>
          pushToDataLayer({ ...basePayload, event: 'Newsletter Signup', newsletter_name: product })
        );

        addToast('Newsletter subscription sent successfully', {
          appearance: 'success',
          autoDismiss: true
        });
      } catch ({ message }) {
        addToast(message, {
          appearance: 'error',
          autoDismiss: true
        });
      }

      setValues({
        additionalTermsAccepted: null,
        countryOfResidence: '',
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        termsAccepted: false,
        checkProductBriefing: false,
        checkProductBusinessNews: false,
        checkProductDeepDive: false,
        checkProductEvents: false,
        checkProductMarketing: false,
        checkProductPodcast: false
      });
    };
    doSubmit();
  }, [addToast, submitState, pushToDataLayer, setValues, submitSalesforceNewsletter]);

  return (
    <form className="newsletter-signup__full-page">
      <h1 className="newsletter-signup__full-page-title">{title}</h1>

      <p className="newsletter-signup__full-page-subtitle">{subtitle}</p>

      <div className="newsletter-signup__full-page-options">
        {productBriefing && (
          <div className={briefingOptionClass}>
            <img
              className="newsletter-signup__full-page-option-icon"
              src="/static/img/newsletters/briefing.png"
            />

            <h2 className="newsletter-signup__full-page-option-title">
              BOAT <span className="newsletter-signup__full-page-option-highlight">BRIEFING</span>
            </h2>

            <p className="newsletter-signup__full-page-option-frequency">Weekdays</p>

            <p className="newsletter-signup__full-page-option-description">
              Stay informed with our daily digest of the latest yacht news and brokerage headlines,
              exclusive yacht features and more
            </p>

            <input
              readOnly
              type="checkbox"
              className="newsletter-signup__full-page-option-checkbox"
              checked={checkProductBriefing}
              name="checkProductBriefing"
              onClick={() => handleCheckboxChange('checkProductBriefing')}
            />

            <label
              className="newsletter-signup__full-page-option-label"
              onClick={() => handleCheckboxChange('checkProductBriefing')}>
              <span className="newsletter-signup__full-page-option-label__plus">+</span>
              <span className="newsletter-signup__full-page-option-label__tick-small" />
              <span className="newsletter-signup__full-page-option-label__tick-large" />
            </label>
          </div>
        )}

        {productDeepDive && (
          <div className={deepDiveOptionClass}>
            <img
              className="newsletter-signup__full-page-option-icon"
              src="/static/img/newsletters/deep-dive.png"
            />

            <h2 className="newsletter-signup__full-page-option-title">
              BOAT <span className="newsletter-signup__full-page-option-highlight">DEEP DIVE</span>
            </h2>

            <p className="newsletter-signup__full-page-option-frequency">Saturdays</p>

            <p className="newsletter-signup__full-page-option-description">
              Submerse yourself in superyachting - from the world's finest yachts to new charter
              destinations and interviews with yacht owners
            </p>

            <input
              readOnly
              type="checkbox"
              className="newsletter-signup__full-page-option-checkbox"
              checked={checkProductDeepDive}
              name="checkProductDeepDive"
              onClick={() => handleCheckboxChange('checkProductDeepDive')}
            />

            <label
              className="newsletter-signup__full-page-option-label"
              onClick={() => handleCheckboxChange('checkProductDeepDive')}>
              <span className="newsletter-signup__full-page-option-label__plus">+</span>
              <span className="newsletter-signup__full-page-option-label__tick-small" />
              <span className="newsletter-signup__full-page-option-label__tick-large" />
            </label>
          </div>
        )}

        {productPodcast && (
          <div className={podcastOptionClass}>
            <img
              className="newsletter-signup__full-page-option-icon"
              src="/static/img/newsletters/podcast.png"
            />

            <h2 className="newsletter-signup__full-page-option-title">
              BOAT{' '}
              <span className="newsletter-signup__full-page-option-highlight">EDITOR'S CHOICE</span>
            </h2>

            <p className="newsletter-signup__full-page-option-frequency">Sundays</p>

            <p className="newsletter-signup__full-page-option-description">
              Five most important stories you may have missed from the week plus the Boat Briefing
              podcast
            </p>

            <input
              readOnly
              type="checkbox"
              className="newsletter-signup__full-page-option-checkbox"
              checked={checkProductPodcast}
              name="checkProductPodcast"
              onClick={() => handleCheckboxChange('checkProductPodcast')}
            />

            <label
              className="newsletter-signup__full-page-option-label"
              onClick={() => handleCheckboxChange('checkProductPodcast')}>
              <span className="newsletter-signup__full-page-option-label__plus">+</span>
              <span className="newsletter-signup__full-page-option-label__tick-small" />
              <span className="newsletter-signup__full-page-option-label__tick-large" />
            </label>
          </div>
        )}

        {productBusinessNews && (
          <div className={businessNewsOptionClass}>
            <img
              className="newsletter-signup__full-page-option-icon"
              src="/static/img/newsletters/business-news.png"
            />

            <h2 className="newsletter-signup__full-page-option-title">
              BOATPro <span className="newsletter-signup__full-page-option-highlight">NEWS</span>
            </h2>

            <p className="newsletter-signup__full-page-option-frequency">Wednesdays</p>

            <p className="newsletter-signup__full-page-option-description">
              Never miss a thing with industry news and analysis of the superyacht sector using data
              from our award-winning intelligence tool, BOATPro
            </p>

            <input
              readOnly
              type="checkbox"
              className="newsletter-signup__full-page-option-checkbox"
              checked={checkProductBusinessNews}
              name="checkProductBusinessNews"
              onClick={() => handleCheckboxChange('checkProductBusinessNews')}
            />

            <label
              className="newsletter-signup__full-page-option-label"
              onClick={() => handleCheckboxChange('checkProductBusinessNews')}>
              <span className="newsletter-signup__full-page-option-label__plus">+</span>
              <span className="newsletter-signup__full-page-option-label__tick-small" />
              <span className="newsletter-signup__full-page-option-label__tick-large" />
            </label>
          </div>
        )}

        {productEvents && (
          <div className={eventsOptionClass}>
            <img
              className="newsletter-signup__full-page-option-icon"
              src="/static/img/newsletters/events.png"
            />

            <h2 className="newsletter-signup__full-page-option-title">
              BOAT <span className="newsletter-signup__full-page-option-highlight">EVENTS</span>
            </h2>

            <p className="newsletter-signup__full-page-option-description">
              Keep up to date with BOAT's latest conferences and events - from the World Superyacht
              Awards to the Superyacht Design Festival
            </p>

            <input
              readOnly
              type="checkbox"
              className="newsletter-signup__full-page-option-checkbox"
              checked={checkProductEvents}
              name="checkProductEvents"
              onClick={() => handleCheckboxChange('checkProductEvents')}
            />

            <label
              className="newsletter-signup__full-page-option-label"
              onClick={() => handleCheckboxChange('checkProductEvents')}>
              <span className="newsletter-signup__full-page-option-label__plus">+</span>
              <span className="newsletter-signup__full-page-option-label__tick-small" />
              <span className="newsletter-signup__full-page-option-label__tick-large" />
            </label>
          </div>
        )}

        {productMarketing && (
          <div className={marketingOptionClass}>
            <img
              className="newsletter-signup__full-page-option-icon"
              src="/static/img/newsletters/marketing.png"
            />

            <h2 className="newsletter-signup__full-page-option-title">
              BOAT <span className="newsletter-signup__full-page-option-highlight">MARKETING</span>
            </h2>

            <p className="newsletter-signup__full-page-option-description">
              Be the first to hear about magazine subscription offers, exclusive discounts and more
            </p>

            <input
              readOnly
              type="checkbox"
              className="newsletter-signup__full-page-option-checkbox"
              checked={checkProductMarketing}
              name="checkProductMarketing"
              onClick={() => handleCheckboxChange('checkProductMarketing')}
            />

            <label
              className="newsletter-signup__full-page-option-label"
              onClick={() => handleCheckboxChange('checkProductMarketing')}>
              <span className="newsletter-signup__full-page-option-label__plus">+</span>
              <span className="newsletter-signup__full-page-option-label__tick-small" />
              <span className="newsletter-signup__full-page-option-label__tick-large" />
            </label>
          </div>
        )}
      </div>

      <div className="newsletter-signup__form newsletter-signup__form--embedded">
        <div className="newsletter-signup__first-and-last-name">
          <Input
            type="text"
            name="firstName"
            placeholder="* First name"
            className="newsletter-signup__input"
            required="required"
            onChange={({ event, value }) => handleChange(event, value)}
            value={firstName}
          />

          <Input
            type="text"
            name="lastName"
            placeholder="* Last name"
            className="newsletter-signup__input"
            required="required"
            onChange={({ event, value }) => handleChange(event, value)}
            value={lastName}
          />
        </div>

        <Input
          type="text"
          name="email"
          placeholder="* Email"
          className="newsletter-signup__input"
          required="required"
          onChange={({ event, value }) => handleChange(event, value)}
          value={email}
        />

        <Select
          name="countryOfResidence"
          className="newsletter-signup__input"
          options={countryOfResidenceOptions}
          onChange={({ value }) => handleCountryOfResidenceChange(value)}
          selected={countryOfResidence}
        />

        <Select
          name="role"
          className="newsletter-signup__input"
          options={roleOptions}
          onChange={({ event, value }) => handleChange(event, value)}
          selected={role}
        />

        <div className="newsletter-signup__checkbox">
          <input
            readOnly
            className="newsletter-signup__checkbox__box"
            type="checkbox"
            checked={termsAccepted}
            name="termsAccepted"
            onClick={() => handleTermsCheckboxChange()}
          />

          <label
            className="newsletter-signup__checkbox__label"
            onClick={() => handleTermsCheckboxChange()}>
            I agree to the{' '}
            <a
              href={PRIVACY.HREF}
              className="newsletter-signup__checkbox__footer-link"
              target="_blank"
              rel="noreferrer">
              terms and conditions and privacy policy
            </a>
            .
          </label>
        </div>

        {showAdditionalTerms && (
          <div className="newsletter-signup__radio">
            <label className="newsletter-signup__radio__label">
              I would like to receive tailored information about events and products from BOAT
              International Media Ltd, an Informa Group Company. You can opt out at any time by
              clicking unsubscribe or contacting us. For Further details, see our{' '}
              <a
                href={PRIVACY.HREF}
                className="newsletter-signup__checkbox__footer-link"
                target="_blank"
                rel="noreferrer">
                privacy policy
              </a>
              .
            </label>

            <div className="newsletter-signup__radio__boxes">
              <input
                readOnly
                type="radio"
                name="additionalTermsAccepted"
                value="yes"
                className="newsletter-signup__radio__box"
                checked={additionalTermsAccepted === true}
                onClick={() => handleAdditionalTermsChange(true)}
              />
              <label
                className="newsletter-signup__radio__box-label"
                onClick={() => handleAdditionalTermsChange(true)}>
                Yes
              </label>{' '}
              <input
                readOnly
                type="radio"
                name="additionalTermsAccepted"
                value="no"
                className="newsletter-signup__radio__box"
                checked={additionalTermsAccepted === false}
                onClick={() => handleAdditionalTermsChange(false)}
              />
              <label
                className="newsletter-signup__radio__box-label"
                onClick={() => handleAdditionalTermsChange(false)}>
                No
              </label>
            </div>
          </div>
        )}

        <Button
          type="button"
          name="button"
          data-gtm-id="newsletter-signup"
          className={buttonClass}
          disabled={!isFormFull}
          onClick={() => setSubmitState({ values, setValues })}>
          Submit
        </Button>
      </div>
    </form>
  );
};

FullPageForm.propTypes = {
  productBriefing: PropTypes.bool.isRequired,
  productBusinessNews: PropTypes.bool.isRequired,
  productDeepDive: PropTypes.bool.isRequired,
  productEvents: PropTypes.bool.isRequired,
  productMarketing: PropTypes.bool.isRequired,
  productPodcast: PropTypes.bool.isRequired,
  subtitle: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
};

export default FullPageForm;
